import { useId } from "react";
import { useFormContext } from "react-hook-form";
import { US_STATES } from "@utils/usStates";
import {
    isUsStateCode,
    type AddressFormValues,
} from "../_helpers/addressForm";
import { FormField, INPUT_CLASS } from "./FormField";

// The address as separate fields (street, city, state, ZIP), shared by the
// Edit Organization (C5) and Edit Location (C6) drawers. It renders inside a
// react-hook-form FormProvider whose values include AddressFormValues.
export const AddressFormFields = () => {
    const {
        register,
        formState: { errors, defaultValues },
    } = useFormContext<AddressFormValues>();
    const id = useId();
    const fieldId = (name: keyof AddressFormValues) => `${id}-${name}`;

    // A stored state the picker does not list (a legacy "PR") is shown by
    // name rather than as a blank, and the schema refuses to save it.
    const storedState = defaultValues?.state ?? "";
    const unmappedState =
        storedState && !isUsStateCode(storedState) ? storedState : null;

    return (
        <fieldset className="flex flex-col gap-4">
            <legend className="mb-2 font-bold">Address</legend>
            <FormField
                id={fieldId("streetAddress")}
                label="Street Address"
                error={errors.streetAddress?.message}
            >
                <input
                    id={fieldId("streetAddress")}
                    type="text"
                    autoComplete="street-address"
                    className={INPUT_CLASS}
                    {...register("streetAddress")}
                />
            </FormField>
            <FormField
                id={fieldId("city")}
                label="City"
                error={errors.city?.message}
            >
                <input
                    id={fieldId("city")}
                    type="text"
                    className={INPUT_CLASS}
                    {...register("city")}
                />
            </FormField>
            <FormField
                id={fieldId("state")}
                label="State"
                error={errors.state?.message}
            >
                {/* A select of USPS codes: the legal-class restrictions
                    match the stored code exactly (§3.12). */}
                <select
                    id={fieldId("state")}
                    className="select select-bordered w-full bg-white text-neutral"
                    {...register("state")}
                >
                    <option value="">Select a state</option>
                    {unmappedState !== null && (
                        <option value={unmappedState}>
                            Unknown value: {unmappedState}
                        </option>
                    )}
                    {US_STATES.map((usState) => (
                        <option key={usState.code} value={usState.code}>
                            {usState.name}
                        </option>
                    ))}
                </select>
            </FormField>
            <FormField
                id={fieldId("zipCode")}
                label="ZIP Code"
                error={errors.zipCode?.message}
            >
                <input
                    id={fieldId("zipCode")}
                    type="text"
                    inputMode="numeric"
                    className={INPUT_CLASS}
                    {...register("zipCode")}
                />
            </FormField>
        </fieldset>
    );
};
